import { NUM_OF_MONTH_COLUMNS_FOR_CAPACITY_REPORT, CapacityTypesPlausibilityCheck } from './constants';
import dayjs from '@utils/dayjs';
import { CapacityType } from '../../../enums/CapacityType';
import { parseStringToFloat } from '../../../utils/numberUtils';
import { CapacityReportType } from '../../../enums/CapacityReportType';
import { getUpcomingMonths } from '../../../utils/dateUtils';
import { DATE_FORMATS } from '@callmart/types';

export const isCapacityReportPresent = (capacityReport1, capacityReport2, capacityTypeName) => {
    return (
        capacityReport1.capacityType.name === capacityTypeName &&
        capacityReport1.task.id === capacityReport2.task.id &&
        capacityReport1.worktype.id === capacityReport2.worktype.id &&
        capacityReport1.channel.id === capacityReport2.channel.id
    );
};

export const getCapacityTypesToCheck = (capacityTypeName, paidTimeInfluentalCapacityTypes) => {
    const capacityTypesToCheck = [];
    if (
        CapacityTypesPlausibilityCheck.CUSTOMER_WORKLOAD.influentialCapacityTypes.some(
            (capacityType) => capacityType.name === capacityTypeName
        )
    ) {
        capacityTypesToCheck.push(CapacityTypesPlausibilityCheck.CUSTOMER_WORKLOAD);
    }

    if (
        CapacityTypesPlausibilityCheck.FTE_PAID_AND_SENIOR_AGENT.influentialCapacityTypes.some(
            (capacityType) => capacityType.name === capacityTypeName
        )
    ) {
        capacityTypesToCheck.push(CapacityTypesPlausibilityCheck.FTE_PAID_AND_SENIOR_AGENT);
    }
    if (paidTimeInfluentalCapacityTypes.some((capacityType) => capacityType.name === capacityTypeName)) {
        capacityTypesToCheck.push({
            mainCapacityType: CapacityType.PAID_TIME.name,
            influentialCapacityTypes: paidTimeInfluentalCapacityTypes,
        });
    }

    return capacityTypesToCheck;
};

export const getPlausibilityCheckValues = (mainCapacityType, capacityTypesToCheck, cellParams, tableRows) => {
    let plausibilityCheck;

    for (const capacityTypeData of capacityTypesToCheck) {
        const capacityReport = tableRows.find((cr) =>
            isCapacityReportPresent(cr, cellParams.data, capacityTypeData.name)
        );

        if (!capacityReport) continue;
        else {
            const key =
                mainCapacityType === CapacityType.PAID_TIME.name
                    ? `${capacityTypeData.name}`
                    : `${capacityTypeData.variable}Value`;

            plausibilityCheck = {
                ...plausibilityCheck,
                mainCapacityType,
                [key]: parseStringToFloat(capacityReport[cellParams.column.colId].value),
            };

            if (mainCapacityType === capacityTypeData.name) {
                plausibilityCheck = {
                    ...plausibilityCheck,
                    mainCapacityReportId: capacityReport[cellParams.column.colId].capacityReportId,
                };
            }
        }
    }

    return { plausibilityCheck };
};

export const getMonthColumns = (firstMonth) => {
    return getUpcomingMonths(firstMonth, NUM_OF_MONTH_COLUMNS_FOR_CAPACITY_REPORT);
};

export const createMapWithTableRows = (list, monthColumns, capacityReportType, rowsHeader) => {
    let rowsMap = new Map();

    for (const item of list) {
        const key = `${item.id}`;

        const capacityReports = {
            capacityId: item.id,
            capacityTypeId: item.capacityTypeId,
            capacityType: item.capacityType,
            worktype: {
                id: item.workingUnitLocation.workingUnit.worktype.id,
                name: item.workingUnitLocation.workingUnit.worktype.worktype,
            },
            channel: {
                id: item.workingUnitLocation.workingUnit.channel.id,
                name: item.workingUnitLocation.workingUnit.channel.name,
            },
            task: {
                id: item.workingUnitLocation.workingUnit.task.id,
                name: item.workingUnitLocation.workingUnit.task.name,
            },
            rowsHeader: rowsHeader,
        };

        monthColumns.forEach((monthColumn) => {
            const month = dayjs(monthColumn).format(DATE_FORMATS.YEAR_MONTH);

            const capacityReport = item[capacityReportType].find((cr) => {
                return dayjs(new Date(cr.date)).format(DATE_FORMATS.YEAR_MONTH) === month;
            });
            const capacityReportValue = capacityReport ? capacityReport.value : null;
            capacityReports[`${month}`] = {
                value: capacityReportValue,
                originalValue: capacityReportValue,
                checked: capacityReport?.checked,
                capacityReportId: capacityReport?.id,
            };
        });

        if (rowsMap.has(key)) {
            rowsMap.set(key, {
                ...rowsMap.get(key),
                ...capacityReports,
            });
        } else {
            rowsMap.set(key, {
                ...capacityReports,
            });
        }
    }

    return rowsMap;
};

export const getDateOptionsForExportDialog = ({ reportType }) => {
    const numOfPreviousMonths = reportType === CapacityReportType.CAPACITY_REPORT ? 6 : 12;
    const previousMonthsStartPoint = reportType === CapacityReportType.CAPACITY_REPORT ? 0 : 1;
    const nextMonthsStartPoint = reportType === CapacityReportType.CAPACITY_REPORT ? 1 : 0;

    const previousMonths = [];
    const nextMonths = [];
    for (let i = previousMonthsStartPoint; i < numOfPreviousMonths; i++) {
        previousMonths.push({
            month: dayjs().subtract(i, 'months').format(DATE_FORMATS.ISO_DATE),
            monthName: dayjs().subtract(i, 'months').format(DATE_FORMATS.MONTH_NAME_YEAR),
        });
    }

    let currentMonth = dayjs().startOf('month');
    const endOfNextYear = dayjs().add(1, 'year').endOf('year').startOf('month');
    const monthsInBetween = endOfNextYear.diff(currentMonth, 'months');
    for (let i = nextMonthsStartPoint; i <= monthsInBetween; i++) {
        nextMonths.push({
            month: dayjs().add(i, 'months').format(DATE_FORMATS.ISO_DATE),
            monthName: dayjs().add(i, 'months').format(DATE_FORMATS.MONTH_NAME_YEAR),
        });
    }

    return { previousMonths, nextMonths };
};
